import PropTypes from 'prop-types';
import React from 'react';
import { stripHTMLTags, insertIf } from '../../../../util/util';
import { imagePropTypes } from '../../Product.propTypes';

/**
 * `ProductMicrodata` is a utility component used to generate JSON-LD microdata for a product.
 */
const ProductMicrodata = ({
  title,
  description_html,
  color,
  size,
  images,
  price,
  currency,
  sku,
  isAvailable
}) => {
  // build schema
  const schemaObject = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: title,
    brand: { '@type': 'Brand', name: 'Bombas' },
    description: stripHTMLTags(description_html),
    ...insertIf(sku, { sku }),
    ...insertIf(color, { color }),
    ...insertIf(size, { size }),
    ...insertIf(images?.length, {
      image: images.map((img) => img.src)
    }),
    ...insertIf(!!price, {
      offers: {
        '@type': 'Offer',
        price,
        priceCurrency: currency,
        availability: isAvailable
          ? 'https://schema.org/InStock'
          : 'https://schema.org/OutOfStock'
      }
    })
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(schemaObject)
      }}
    />
  );
};

ProductMicrodata.propTypes = {
  /**
   * Product title
   */
  title: PropTypes.string.isRequired,
  /**
   * Product description as HTML
   */
  description_html: PropTypes.string.isRequired,
  /**
   * If `true`, the product is available.
   */
  isAvailable: PropTypes.bool.isRequired,
  /**
   * Product color
   */
  color: PropTypes.string,
  /**
   * Product size
   */
  size: PropTypes.string,
  /**
   * Product price
   */
  price: PropTypes.string,
  /**
   * Country Currency of Price
   */
  currency: PropTypes.string,
  /**
   * Product SKU
   */
  sku: PropTypes.string,
  /**
   * List of product images, grouped by color.
   */
  images: PropTypes.arrayOf(PropTypes.shape(imagePropTypes))
};

ProductMicrodata.defaultProps = {
  color: null,
  size: null,
  price: null,
  currency: null,
  sku: null,
  images: []
};

export default ProductMicrodata;
